import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { HinDecodeResult, parseHin } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';

export type MarineVesselFieldRole = 'hin' | 'manufacturer' | 'serial' | 'buildMonth' | 'modelYear' | 'certYear';

/** Value keys of the configured fields that each vessel detail is written to. */
export type MarineVesselFieldKeys = Partial<Record<MarineVesselFieldRole, string>>;

/** Which vessel detail a field holds, judged by its label, property name or value key. */
export function getMarineVesselFieldRole(labels: Array<string | null | undefined>): MarineVesselFieldRole | null {
  const values = labels
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map(value => value.trim().toLowerCase());

  if (values.some(value => value === 'hin' || value.endsWith('hin'))) return 'hin';
  if (values.some(value => value === 'manufacturer' || value.endsWith('manufacturer'))) return 'manufacturer';
  if (values.some(value => value === 'serial' || value.endsWith('serial') || value === 'serial number')) {
    return 'serial';
  }
  if (values.some(value => value === 'build month' || value.endsWith('buildmonth') || value.endsWith('build_month'))) {
    return 'buildMonth';
  }
  if (values.some(value => value === 'model year' || value.endsWith('modelyear') || value.endsWith('model_year'))) {
    return 'modelYear';
  }
  if (values.some(value => value === 'cert year' || value.endsWith('certyear') || value.endsWith('cert_year'))) {
    return 'certYear';
  }

  return null;
}

export function normalizeHin(hin: string): string {
  return (hin || '').trim().toUpperCase();
}

export function hinValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = normalizeHin(String(control.value ?? ''));
    if (!value) return null;
    if (value.length !== 12) return { invalidHin: true };

    return parseHin(value) ? null : { invalidHin: true };
  };
}

/** Maps decoded values onto whichever configured fields hold each vessel detail. */
export function buildHinPatch(
  keys: MarineVesselFieldKeys,
  decoded: HinDecodeResult,
  hin: string
): Record<string, string | number | null> {
  const patch: Record<string, string | number | null> = {};
  const set = (key: string | undefined, value: string | number | null) => {
    if (key) patch[key] = value;
  };

  set(keys.hin, hin);
  set(keys.manufacturer, decoded.manufacturer || null);
  set(keys.serial, decoded.serialNumber || null);
  set(keys.buildMonth, decoded.buildMonth || null);
  set(keys.modelYear, decoded.modelYear || null);
  set(keys.certYear, decoded.certYear || null);

  return patch;
}

/**
 * Decodes HINs as they're typed. Remembers the last HIN it decoded so repeated value changes don't re-decode,
 * and drops a lookup whose HIN was replaced while the manufacturer table was loading.
 */
export class HinDecoder {
  constructor(private toastSvc: ToastService) {}

  lastDecodedHin: string | null = null;

  /** Resolves with the decoded HIN, or null when there's nothing (new) to apply. */
  async decode(rawHin: string): Promise<{ hin: string; decoded: HinDecodeResult } | null> {
    const hin = normalizeHin(rawHin);

    if (!hin || hin.length !== 12) {
      this.lastDecodedHin = null;
      return null;
    }

    if (this.lastDecodedHin === hin) return null;

    const parsed = parseHin(hin);
    if (!parsed) {
      this.lastDecodedHin = null;
      this.toastSvc.show({
        title: 'Invalid HIN',
        text: 'Could not decode this HIN. Verify the value and try again.',
        type: 'warning',
      });
      return null;
    }

    this.lastDecodedHin = hin;

    // Loaded lazily: the MIC table is ~500 KB and would otherwise sit in the initial bundle.
    let MIC_LOOKUP: Record<string, string>;
    try {
      ({ MIC_LOOKUP } = await import('@vsolv/packages/properties/domain/mic-lookup'));
    } catch {
      // Network error, or a stale tab requesting a chunk the latest deploy no longer serves.
      if (this.lastDecodedHin !== hin) return null;

      this.lastDecodedHin = null;
      this.toastSvc.show({
        title: 'Manufacturer lookup unavailable',
        text: 'Could not load the manufacturer list. Check your connection or reload the page, then re-enter the HIN.',
        type: 'warning',
      });
      return null;
    }

    // The HIN changed while the table was loading; that newer value owns the form now.
    if (this.lastDecodedHin !== hin) return null;

    const manufacturer = MIC_LOOKUP[parsed.mic];
    if (!manufacturer) {
      this.toastSvc.show({
        title: 'Manufacturer not found',
        text: `Manufacturer code ${parsed.mic} was not recognized.`,
        type: 'warning',
      });
    }

    return { hin, decoded: { ...parsed, manufacturer: manufacturer ?? parsed.mic } };
  }
}
